import type { FolderStatus } from '@ralph/contracts'
import { PROVIDER_LABEL, SURFACE_LABEL } from '@ralph/contracts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * What the folders domain's refusals say, in Brazilian Portuguese (checked against the English
 * shape at compile time).
 *
 * Register is `você`, informal-professional. Vocabulário ratificado (ADR-0018): *pasta
 * conectada* (f.) — nunca *diretório*, nunca *ficheiro*; *base de conhecimento* (f.); *conta*
 * (f.); *arquivo* (m.), que é a palavra brasileira. Onde o inglês nomeia um administrador em
 * abstrato, aqui se diz *quem administra*, para não adivinhar o gênero de alguém (D-21).
 *
 * ── Concordância, e por que os rótulos de status são femininos ──
 *
 * Tudo aqui fala de UMA pasta, que é feminina, então os particípios concordam com ela:
 * "pausada", "parada". Os nomes de produto vêm de `PROVIDER_LABEL` / `SURFACE_LABEL` no
 * contrato — Microsoft, OneDrive, Google Drive e Amazon S3 não se traduzem, e todos são
 * masculinos em português, o que é o que sustenta o "do" antes deles.
 *
 * ── Termos de arte que ficam em inglês (D-15) ──
 *
 * `OAuth`, `offline_access`, `endpoint`, `token`, `tenant`, `CONNECTOR_S3_ENDPOINT_HOSTS`. Um
 * nome de variável de ambiente é um identificador, não uma frase.
 */
type FoldersMessages = Pick<Messages['errors'], Extract<keyof Messages['errors'], `folders.${string}`>>

/** Como a pasta está, nas palavras que a aba de pastas conectadas já usa para os mesmos cinco estados. */
const standing: Record<FolderStatus, string> = {
  previewing: 'ainda não iniciada',
  active: 'acompanhando as mudanças',
  paused: 'pausada',
  needs_reauth: 'esperando uma nova entrada na conta',
  error: 'parada depois de uma verificação que falhou'
}

export const folders: FoldersMessages = {
  // --- a pasta em si --------------------------------------------------------
  'folders.not_found': () => 'Essa pasta conectada não existe.',
  'folders.provider_surface_mismatch': ({ provider, surface }) =>
    `Uma conta ${PROVIDER_LABEL[provider]} não pode acompanhar uma pasta do ${SURFACE_LABEL[surface]}.`,
  'folders.cannot_start_in_status': ({ status }) => `Esta pasta está ${standing[status]}, então não há o que iniciar.`,
  'folders.not_watching': ({ status }) => `Esta pasta está ${standing[status]}, então não há o que pausar.`,
  'folders.cannot_resume_in_status': ({ status }) => `Esta pasta está ${standing[status]}, então não há o que retomar.`,
  'folders.unknown_cadence': () => 'Escolha uma das frequências oferecidas.',
  'folders.provider_must_match': () => 'Uma pasta só pode passar para uma conta do mesmo provedor.',
  'folders.account_cannot_open_folder': () => 'Essa conta não consegue abrir esta pasta.',
  'folders.file_not_in_folder': () => 'Esse arquivo não está em uma pasta conectada.',
  'folders.page_expired': () => 'Essa página expirou. Abra a pasta de novo.',

  // --- quem vai poder buscar no que a pasta carrega -------------------------
  'folders.acknowledge_everyone': () =>
    'Esta base de conhecimento está aberta para todas as pessoas que entram. Confirme que tudo o que for incluído aqui poderá ser buscado por todas elas.',
  'folders.acknowledge_audience': () => 'Confirme quem vai poder buscar no que esta pasta carrega.',
  'folders.audience_widened': () =>
    'Mais pessoas podem usar esta base de conhecimento do que quando esta pasta foi confirmada. Confirme quem pode buscar no que ela carrega e verifique de novo.',

  // --- a conta pela qual a pasta lê -----------------------------------------
  'folders.account_not_connected': () => 'Essa conta não está conectada aqui.',
  'folders.account_disconnected': () => 'Essa conta foi desconectada.',
  'folders.account_disconnected_reconnect': () => 'Essa conta foi desconectada. Conecte-a de novo como uma conta nova.',
  'folders.account_already_disconnected': () => 'Essa conta já está desconectada.',
  'folders.folder_account_disconnected': () =>
    'A conta que esta pasta usava foi desconectada. Escolha outra conta para ela primeiro.',
  'folders.account_is_someone_elses': () =>
    'Essa conta é a entrada de outra pessoa. Só ela, ou quem administra, pode ler por meio dela.',
  'folders.company_account_admin_only': () =>
    'Essa é uma conta da empresa. Só quem administra pode conectar pastas por meio dela.',
  'folders.only_administrators_add_company_accounts': () => 'Só quem administra pode adicionar uma conta da empresa.',
  'folders.only_owner_or_administrator_renews': () =>
    'Só a pessoa que conectou esta conta, ou quem administra, pode entrar nela de novo.',
  'folders.only_owner_or_administrator_removes': () =>
    'Só a pessoa que conectou esta conta, ou quem administra, pode removê-la.',
  'folders.account_cannot_be_renewed': () => 'Essa conta não pode ser renovada com esta entrada.',

  // --- entrar na conta de um provedor ---------------------------------------
  'folders.sign_in_not_configured': ({ provider }) =>
    `A entrada pela ${PROVIDER_LABEL[provider]} não está configurada nesta instalação.`,
  // As duas frases sobre a entrada já concluída e a entrada expirada são `auth.*`. Aqui fica
  // o que nomeia um provedor, coisa que a de `auth` não faz.
  'folders.sign_in_other_browser': () => 'Esta entrada começou em outro navegador. Comece de novo pela aba de pastas.',
  'folders.sign_in_refused_by_provider': ({ provider }) => `A ${PROVIDER_LABEL[provider]} não concluiu a entrada.`,
  'folders.sign_in_no_code': () => 'A entrada voltou sem um código de autorização.',
  'folders.sign_in_not_renewable_google': () =>
    'A Google não emitiu uma entrada renovável. Remova esta plataforma dos apps conectados da sua conta Google e entre de novo.',
  'folders.sign_in_not_renewable_microsoft': () =>
    'A Microsoft não emitiu uma entrada renovável. Verifique se o registro do aplicativo permite offline_access.',
  'folders.signed_in_as_other_account': ({ signedInAs, expected }) =>
    `Essa entrada foi como ${signedInAs}, mas esta conta é ${expected}. Entre como ${expected} para renová-la, ou conecte ${signedInAs} como uma conta nova.`,

  // --- credenciais que esta plataforma se recusa a guardar ------------------
  'folders.scope_can_write': ({ scopes }) =>
    `O provedor concedeu permissão de escrita (${scopes}). Esta plataforma só lê, e não guarda uma credencial capaz de escrever. Corrija o registro do aplicativo ou o consentimento e entre de novo.`,
  'folders.tenant_consent_can_write': ({ scopes }) =>
    `O tenant consentiu permissões de aplicativo capazes de escrever (${scopes}). Esta plataforma só lê. Remova-as do registro do aplicativo e conecte de novo.`,
  'folders.s3_endpoint_not_allowed': ({ host }) =>
    `${host} não é um endpoint S3 que esta instalação possa alcançar. Quem opera a plataforma adiciona o host em CONNECTOR_S3_ENDPOINT_HOSTS.`
}
